import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { maskMoneyInput, parseInputToApi } from '@/lib/money';
import { Modal, Field } from '@/features/transactions/TransactionForm';
import type { ReferenceData } from '@/features/transactions/types';

type Line = { entityId: string; percentage: string; income: string };

const MODES = [
  { value: 'igual', label: 'Igual entre todos', hint: 'Metade para cada, num casal.' },
  { value: 'percentual', label: 'Percentual fixo', hint: 'Cotas definidas por você.' },
  {
    value: 'proporcional_renda',
    label: 'Proporcional à renda',
    hint: 'Quem ganha mais paga proporcionalmente mais.',
  },
];

/**
 * Grupo de compartilhamento — RF-CAS-01.
 *
 * AS COTAS PRECISAM SOMAR 100%. Com 90%, o que sobra fica sem dono e o saldo do
 * acerto deixa de fechar em zero — o formulario calcula a soma na tela para que
 * o problema apareca antes de salvar, e nao numa mensagem do servidor.
 */
export function SharingGroupForm({
  reference,
  selectedIds,
  onClose,
  onSaved,
}: {
  reference: ReferenceData;
  selectedIds: number[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const people = reference.entities;

  const [name, setName] = useState('Casal');
  const [mode, setMode] = useState('igual');
  const [frequency, setFrequency] = useState('mensal');
  const [lines, setLines] = useState<Line[]>([
    { entityId: String(people[0]?.id ?? ''), percentage: '50', income: '' },
    { entityId: String(people[1]?.id ?? ''), percentage: '50', income: '' },
  ]);

  const filled = lines.filter((l) => l.entityId !== '');
  const total = filled.reduce((acc, l) => acc + Number(l.percentage.replace(',', '.') || 0), 0);
  const balanced = Math.abs(total - 100) < 0.0001;
  const duplicated = new Set(filled.map((l) => l.entityId)).size !== filled.length;

  function patch(index: number, changes: Partial<Line>) {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...changes } : l)));
  }

  /** Divide 100% igualmente, com o resíduo na primeira — mesma regra do rateio. */
  function splitEvenly(count: number): string[] {
    const base = Math.floor((100 / count) * 1e6) / 1e6;
    const shares = Array.from({ length: count }, () => base);
    shares[0] = Number((100 - base * (count - 1)).toFixed(6));
    return shares.map((s) => String(s));
  }

  function applyEqual() {
    const shares = splitEvenly(filled.length || 1);
    setLines((prev) => prev.map((l, i) => ({ ...l, percentage: shares[i] ?? l.percentage })));
  }

  /** RF-CAS-01 — cota proporcional à renda informada. */
  function applyByIncome() {
    const incomes = lines.map((l) => Number(parseInputToApi(l.income || '0')));
    const sum = incomes.reduce((a, b) => a + b, 0);

    if (sum <= 0) {
      window.alert('Informe a renda de cada participante para calcular a proporção.');
      return;
    }

    let allocated = 0;

    setLines((prev) =>
      prev.map((l, i) => {
        const isLast = i === prev.length - 1;
        const share = isLast
          ? Number((100 - allocated).toFixed(6))
          : Number(((incomes[i]! / sum) * 100).toFixed(6));
        allocated += share;
        return { ...l, percentage: String(share) };
      }),
    );
  }

  const save = useMutation({
    mutationFn: () =>
      api.post('/sharing-groups', {
        name,
        split_mode: mode,
        settlement_frequency: frequency,
        members: filled.map((l) => ({
          member_entity_id: Number(l.entityId),
          default_share_percentage: Number(l.percentage.replace(',', '.')),
          ...(l.income ? { reference_income: parseInputToApi(l.income) } : {}),
        })),
      }, { entityIds: selectedIds }),
    onSuccess: onSaved,
  });

  const error = save.error instanceof ApiError ? save.error : null;

  const canSubmit =
    name.trim().length >= 2 && filled.length >= 2 && balanced && ! duplicated;

  return (
    <Modal title="Novo grupo de compartilhamento" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <p>{error.message}</p>
          </div>
        )}

        <Field label="Nome" required>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
            autoFocus
            placeholder="Casal, Família, República…"
            className="input w-full"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Como dividir" required>
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="input w-full">
              {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </Field>
          <Field label="Acerto">
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="input w-full"
            >
              <option value="mensal">Mensal</option>
              <option value="quinzenal">Quinzenal</option>
              <option value="sob_demanda">Sob demanda</option>
            </select>
          </Field>
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400">
          {MODES.find((m) => m.value === mode)?.hint}
        </p>

        <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Participantes
            </p>
            <div className="flex gap-2 text-xs">
              <button type="button" onClick={applyEqual} className="text-indigo-600 hover:underline">
                dividir igual
              </button>
              {mode === 'proporcional_renda' && (
                <button type="button" onClick={applyByIncome} className="text-indigo-600 hover:underline">
                  calcular pela renda
                </button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            {lines.map((line, index) => (
              <div key={index} className="flex gap-2">
                <select
                  value={line.entityId}
                  onChange={(e) => patch(index, { entityId: e.target.value })}
                  className="input min-w-0 flex-1"
                >
                  <option value="">Selecione</option>
                  {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>

                {mode === 'proporcional_renda' && (
                  <input
                    value={line.income}
                    onChange={(e) => patch(index, { income: maskMoneyInput(e.target.value) })}
                    inputMode="numeric"
                    placeholder="Renda"
                    className="input w-24 text-right"
                  />
                )}

                <div className="flex w-20 items-center gap-1">
                  <input
                    value={line.percentage}
                    onChange={(e) => patch(index, { percentage: e.target.value.replace(/[^\d.,]/g, '') })}
                    inputMode="decimal"
                    className="input w-full text-right"
                  />
                  <span className="text-xs text-slate-400">%</span>
                </div>

                {lines.length > 2 && (
                  <button
                    type="button"
                    onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                    className="px-1 text-rose-500"
                    aria-label="Remover"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="mt-2 flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => setLines((prev) => [...prev, { entityId: '', percentage: '0', income: '' }])}
              className="text-indigo-600 hover:underline dark:text-indigo-400"
            >
              + Participante
            </button>
            <span className={balanced ? 'text-slate-500 dark:text-slate-400' : 'font-medium text-rose-600'}>
              Soma: {total.toFixed(2).replace('.', ',')}%
            </span>
          </div>

          {/* O erro aparece antes de salvar, não numa mensagem do servidor. */}
          {! balanced && filled.length >= 2 && (
            <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
              As cotas precisam somar 100%. O que sobra ou falta ficaria sem dono, e o saldo do
              acerto deixaria de fechar em zero.
            </p>
          )}

          {duplicated && (
            <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
              Há participante repetido.
            </p>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit || save.isPending}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {save.isPending ? 'Criando…' : 'Criar grupo'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
